import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const fonts = [
  { name: 'Noto Sans', family: '"Noto Sans", -apple-system, sans-serif', type: 'sans', note: 'Scientifically tested: fastest reading speed + best comprehension (CHI 2020 study winner)' },
  { name: 'Lato', family: '"Lato", -apple-system, sans-serif', type: 'sans', note: 'Scientifically tested: top performer for speed reading + comprehension' },
  { name: 'Atkinson Hyperlegible', family: '"Atkinson Hyperlegible", -apple-system, sans-serif', type: 'sans', note: 'Scientifically optimized for readability, designed for low vision' },
  { name: 'Mona Sans', family: '"Mona Sans", -apple-system, sans-serif', type: 'sans', note: 'GitHub\'s variable font, industrial-era grotesque' },
  { name: 'Anybody', family: '"Anybody", -apple-system, sans-serif', type: 'sans', note: '90 styles, variable width, high x-height' },
  { name: 'Literata', family: '"Literata", Georgia, serif', type: 'serif', note: 'Variable font, designed for Google Play Books' },
  { name: 'Shantell Sans', family: '"Shantell Sans", -apple-system, sans-serif', type: 'display', note: 'Variable marker-style font' },
  { name: 'Anek Latin', family: '"Anek Latin", -apple-system, sans-serif', type: 'sans', note: 'Generous curvature, unique' },
  { name: 'Crimson Pro', family: '"Crimson Pro", Georgia, serif', type: 'serif', note: 'Classic book typeface' },
  { name: 'Crimson Text', family: '"Crimson Text", Georgia, serif', type: 'serif', note: 'Traditional serif, readable' },
  { name: 'Mulish', family: '"Mulish", -apple-system, sans-serif', type: 'sans', note: 'Less common, well-spaced' },
  { name: 'Rubik', family: '"Rubik", -apple-system, sans-serif', type: 'sans', note: 'Bold, geometric' },
  { name: 'Playfair Display', family: '"Playfair Display", Georgia, serif', type: 'serif', note: 'Elegant, high contrast' },
  { name: 'Inter', family: 'Inter, -apple-system, sans-serif', type: 'sans', note: 'Highly readable, tall x-height (common)' },
  { name: 'Spectral', family: '"Spectral", Georgia, serif', type: 'serif', note: 'Great for long-form reading' },
  { name: 'IBM Plex Sans', family: '"IBM Plex Sans", -apple-system, sans-serif', type: 'sans', note: 'Clean, professional' },
  { name: 'Space Grotesk', family: '"Space Grotesk", -apple-system, sans-serif', type: 'sans', note: 'Geometric, distinctive' },
];

function VisualExplore() {
  const [isDark, setIsDark] = useState(false);
  const [currentFont, setCurrentFont] = useState(0);

  const bgColor = isDark ? 'bg-black' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-black';
  const textMuted = isDark ? 'text-white/50' : 'text-black/50';
  const textSubtle = isDark ? 'text-white/30' : 'text-black/30';
  const borderColor = isDark ? 'border-white/5' : 'border-black/5';
  const borderHover = isDark ? 'border-white/10' : 'border-black/10';

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} transition-colors duration-300`} style={{ fontFamily: fonts[currentFont].family }}>
      <SEO />
      <div className="max-w-[60ch] mx-auto px-6 py-20">
        <header className="mb-24 flex items-center justify-between">
          <Link to="/" className={`${textSubtle} hover:${textColor} text-xs mb-8 inline-block transition-colors`}>
            ← back
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <select
                value={currentFont}
                onChange={(e) => setCurrentFont(Number(e.target.value))}
                className={`${bgColor} ${textColor} ${borderColor} border text-xs px-3 py-1 rounded cursor-pointer focus:outline-none`}
                style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}
              >
                {fonts.map((font, i) => (
                  <option key={i} value={i}>
                    {font.name}
                  </option>
                ))}
              </select>
              {fonts[currentFont].note && (
                <span className={`${textSubtle} text-[10px] mt-1`}>{fonts[currentFont].note}</span>
              )}
            </div>
            <button
              onClick={() => setIsDark(!isDark)}
              className={`${textSubtle} hover:${textColor} text-xs uppercase tracking-wider transition-colors`}
              style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}
            >
              {isDark ? 'light' : 'dark'}
            </button>
          </div>
        </header>

        {/* Real content, not examples */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="mb-32"
        >
          <h1 className={`text-6xl font-light tracking-tight mb-8 leading-[1.1] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            What makes something feel right
          </h1>
          <p className={`${textMuted} text-lg leading-[1.7] max-w-[52ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            It's not about features. It's about the space between things. The rhythm. The way your eye moves without thinking about it.
          </p>
        </motion.div>

        {/* Hanging punctuation - shown, not explained */}
        <div className="mb-24">
          <p className={`${textMuted} leading-[1.7] max-w-[52ch]`} style={{ textIndent: '-0.4em', paddingLeft: '0.4em', fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            "When punctuation hangs outside the text block, the edge feels cleaner. Your eye doesn't get caught on the quotation mark."
          </p>
        </div>

        {/* Real example with dashes */}
        <div className="mb-24">
          <p className={`${textMuted} leading-[1.7] max-w-[52ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            The em dash—this one—creates a pause. Not dramatic. Just a breath. The en dash connects ranges like 1945–1975. Hyphens link words together. Each has purpose.
          </p>
        </div>

        {/* Old style figures in context */}
        <div className={`mb-32 border-t ${borderColor} pt-16`}>
          <div className={`text-4xl font-light mb-6 ${textColor}`} style={{ fontFeatureSettings: '"onum" 1, "liga" 1, "calt" 1' }}>
            January 15, 2025
          </div>
          <p className={`${textMuted} text-sm leading-[1.6]`} style={{ fontFeatureSettings: '"onum" 1, "liga" 1, "calt" 1' }}>
            Old style figures don't shout. They sit quietly in the text, like lowercase letters. The date feels part of the sentence, not separate from it.
          </p>
        </div>

        {/* Letter spacing - shown in real context */}
        <div className="mb-32">
          <div className={`text-xl mb-12 ${textColor}`} style={{ letterSpacing: '0.06em', fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            ALL CAPS NEED SPACE
          </div>
          <p className={`${textMuted} leading-[1.7] max-w-[48ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            Without spacing, caps feel cramped. With it, they breathe. The space between letters matters as much as the letters themselves.
          </p>
        </div>

        {/* Baseline grid - felt, not shown */}
        <div className="mb-32 space-y-16">
          <div>
            <div className={`text-3xl font-light mb-6 leading-[1.3] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              Everything aligns
            </div>
            <p className={`${textMuted} leading-[1.7]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              When elements sit on the same baseline, something clicks. You might not notice it consciously, but your eye feels the order.
            </p>
          </div>
          
          <div>
            <div className={`text-3xl font-light mb-6 leading-[1.3] ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              The invisible structure
            </div>
            <p className={`${textMuted} leading-[1.7]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
              Good design has bones you can't see. The grid isn't decoration—it's the foundation everything else sits on.
            </p>
          </div>
        </div>

        {/* Small caps - subtle, not explained */}
        <div className="mb-32">
          <p className={`${textMuted} leading-[1.7] max-w-[50ch] mb-8`} style={{ fontFeatureSettings: '"smcp" 1, "liga" 1, "calt" 1', textTransform: 'lowercase' }}>
            Small caps add emphasis without breaking the flow. They maintain the color of the text while still standing out. Subtle distinction.
          </p>
          <p className={`${textSubtle} text-xs`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            The paragraph above uses small caps. Did you notice?
          </p>
        </div>

        {/* Word spacing - the most important thing */}
        <div className={`mb-32 border-t ${borderColor} pt-16`}>
          <p className={`${textMuted} leading-[1.7] max-w-[52ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            Word spacing is everything. Too tight and words merge into each other. Too open and reading becomes work. The space between words matters more than the words themselves.
          </p>
        </div>

        {/* Restraint */}
        <div className="mb-32">
          <div className="grid grid-cols-3 gap-6">
            <div className={`aspect-square border ${borderColor}`}></div>
            <div className={`aspect-square border ${borderColor} rounded-full`}></div>
            <div className={`aspect-square border ${borderColor}`}></div>
          </div>
        </div>

        {/* Lines - minimal */}
        <div className="mb-32 space-y-16">
          <div className={`h-px ${isDark ? 'bg-white/5' : 'bg-black/5'}`}></div>
          <div className="flex items-center gap-3">
            <div className={`flex-1 h-px ${isDark ? 'bg-white/5' : 'bg-black/5'}`}></div>
            <div className={`w-1 h-1 ${isDark ? 'bg-white/20' : 'bg-black/20'} rounded-full`}></div>
            <div className={`flex-1 h-px ${isDark ? 'bg-white/5' : 'bg-black/5'}`}></div>
          </div>
        </div>

        {/* Interaction - subtle */}
        <SubtleInteraction isDark={isDark} textColor={textColor} textMuted={textMuted} textSubtle={textSubtle} borderColor={borderColor} borderHover={borderHover} />

        {/* Motion - purposeful */}
        <PurposefulMotion isDark={isDark} textMuted={textMuted} borderColor={borderColor} />

        {/* The point */}
        <div className={`mb-32 border-t ${borderColor} pt-16`}>
          <p className={`${textMuted} leading-[1.7] max-w-[50ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            None of this matters if it doesn't serve the content. Every choice should make reading easier, not harder. The best design is invisible.
          </p>
        </div>

        {/* Typography Resources */}
        <div className={`mb-32 border-t ${borderColor} pt-16`}>
          <h2 className={`text-2xl font-light mb-12 ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            Resources
          </h2>
          
          <div className="space-y-12">
            {/* Courses */}
            <div>
              <div className={`text-sm uppercase tracking-wider mb-6 ${textSubtle}`}>Courses</div>
              <div className="space-y-6">
                <ResourceLink 
                  href="https://betterwebtype.com"
                  title="Better Web Type"
                  description="7-day free email course on web typography"
                  textMuted={textMuted}
                  textColor={textColor}
                />
                <ResourceLink 
                  href="https://coursera.org/learn/typography"
                  title="Introduction to Typography"
                  description="CalArts course on Coursera (free to audit)"
                  textMuted={textMuted}
                  textColor={textColor}
                />
                <ResourceLink 
                  href="https://robofont.com/documentation/tutorials"
                  title="RoboFont Tutorials"
                  description="Free type design tutorials and guides"
                  textMuted={textMuted}
                  textColor={textColor}
                />
              </div>
            </div>

            {/* Books */}
            <div>
              <div className={`text-sm uppercase tracking-wider mb-6 ${textSubtle}`}>Books</div>
              <div className="space-y-6">
                <ResourceLink 
                  href="https://www.gutenberg.org/ebooks/54476"
                  title="The Art & Practice of Typography"
                  description="Edmund G. Gress (1917) — Historical overview and practical examples"
                  textMuted={textMuted}
                  textColor={textColor}
                />
                <ResourceLink 
                  href="https://smallpressblog.wordpress.com/wp-content/uploads/2017/11/bringhurstelementsselections1.pdf"
                  title="The Elements of Typographic Style"
                  description="Robert Bringhurst — Classic reference on typography principles"
                  textMuted={textMuted}
                  textColor={textColor}
                />
                <ResourceLink 
                  href="https://gwern.net/doc/design/typography/1926-bartels-theartofspacing.pdf"
                  title="The Art of Spacing"
                  description="Samuel A. Bartels (1926) — Focused on white space and spacing"
                  textMuted={textMuted}
                  textColor={textColor}
                />
              </div>
            </div>

            {/* Tools & References */}
            <div>
              <div className={`text-sm uppercase tracking-wider mb-6 ${textSubtle}`}>Tools & References</div>
              <div className="space-y-6">
                <ResourceLink 
                  href="https://opentypecookbook.com"
                  title="OpenType Cookbook"
                  description="Free guide to OpenType features and advanced techniques"
                  textMuted={textMuted}
                  textColor={textColor}
                />
                <ResourceLink 
                  href="https://archive.flossmanuals.net/fontforge"
                  title="FontForge Manual"
                  description="Free font design tool and comprehensive manual"
                  textMuted={textMuted}
                  textColor={textColor}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mode thoughts */}
        <div className={`mb-16 border-t ${borderColor} pt-16`}>
          <p className={`${textMuted} text-sm leading-[1.6] max-w-[48ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            {isDark ? (
              <>Dark feels intimate. Like late night coding sessions. But does it serve the words, or distract from them?</>
            ) : (
              <>Light feels timeless. Like paper. But does it feel too safe? Too expected?</>
            )}
          </p>
        </div>

      </div>
    </div>
  );
}

function SubtleInteraction({ isDark, textColor, textMuted, textSubtle, borderColor, borderHover }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div className="mb-32">
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`border ${hovered ? borderHover : borderColor} p-8 cursor-pointer transition-all duration-200`}
        style={{
          transform: hovered ? 'translateY(-1px)' : 'translateY(0)'
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className={`text-lg font-light ${textColor}`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
            Hover
          </div>
          <div className={`${textSubtle} text-sm`}>→</div>
        </div>
        <p className={`${textMuted} text-sm`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
          {hovered ? 'Subtle feedback. Nothing dramatic.' : 'The smallest response can feel right.'}
        </p>
      </div>
    </div>
  );
}

function PurposefulMotion({ isDark, textMuted, borderColor }) {
  return (
    <div className="mb-32">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.5, ease: [0.2, 0, 0.38, 0.9] }}
        className={`border ${borderColor} p-12`}
      >
        <p className={`${textMuted} leading-[1.7] max-w-[48ch]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
          Motion should feel natural. Like something settling into place, not performing. When it's right, you don't notice it.
        </p>
      </motion.div>
    </div>
  );
}

function ResourceLink({ href, title, description, textMuted, textColor }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
    >
      <div className={`text-lg font-light mb-1 ${textColor} group-hover:opacity-70 transition-opacity`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
        {title}
      </div>
      <p className={`${textMuted} text-sm leading-[1.6]`} style={{ fontFeatureSettings: '"liga" 1, "calt" 1' }}>
        {description}
      </p>
    </a>
  );
}

export default VisualExplore;
